/**
 * Help-mode keys for the board's KPI tiles and Action Center items. The keys are string literals inside
 * object literals (the help key-coverage scan only accepts literals); the components look them up by
 * tile key / action id. Texts live in client/src/help/dashboardTiles.ts.
 */

/** KPI tile key (see BoardKpiStrip loaders and workSummary.ts) to its help key. */
export const KPI_HELP: Record<string, { helpKey: string }> = {
    'att.present': { helpKey: 'dashboard.kpi.presentToday' },
    'att.leave': { helpKey: 'dashboard.kpi.onLeaveToday' },
    'att.absent': { helpKey: 'dashboard.kpi.absentToday' },
    'att.rate': { helpKey: 'dashboard.kpi.attendanceRate' },
    'leave.pending': { helpKey: 'dashboard.kpi.pendingLeave' },
    'leave.approved': { helpKey: 'dashboard.kpi.leaveApproved' },
    'leave.rejected': { helpKey: 'dashboard.kpi.leaveRejected' },
    'leave.today': { helpKey: 'dashboard.kpi.onLeaveToday' },
    'people.active': { helpKey: 'dashboard.kpi.activeHeadcount' },
    'people.inactive': { helpKey: 'dashboard.kpi.inactiveMembers' },
    'people.noDept': { helpKey: 'dashboard.kpi.noDepartment' },
    'people.departments': { helpKey: 'dashboard.kpi.departments' },
    'acc.cash': { helpKey: 'dashboard.kpi.cashBank' },
    'acc.ar': { helpKey: 'dashboard.kpi.receivable' },
    'acc.ap': { helpKey: 'dashboard.kpi.payable' },
    'acc.overdue': { helpKey: 'dashboard.kpi.overdue30' },
    'acc.income': { helpKey: 'dashboard.kpi.incomeMonth' },
    'acc.expense': { helpKey: 'dashboard.kpi.expenseMonth' },
    'acc.net': { helpKey: 'dashboard.kpi.netMonth' },
    'sales.mtd': { helpKey: 'dashboard.kpi.salesMtd' },
    'sales.pipeline': { helpKey: 'dashboard.kpi.openPipeline' },
    'sales.deals': { helpKey: 'dashboard.kpi.openDeals' },
    'sales.win': { helpKey: 'dashboard.kpi.quoteWinRate' },
    'inv.value': { helpKey: 'dashboard.kpi.stockValue' },
    'inv.items': { helpKey: 'dashboard.kpi.itemCount' },
    'inv.low': { helpKey: 'dashboard.kpi.lowStock' },
    'inv.stores': { helpKey: 'dashboard.kpi.storesWithStock' },
    'req.pending': { helpKey: 'dashboard.kpi.pendingRequisitions' },
    'pay.net': { helpKey: 'dashboard.kpi.lastPayrollNet' },
    'pay.headcount': { helpKey: 'dashboard.kpi.employeesPaid' },
    'pay.avg': { helpKey: 'dashboard.kpi.avgNetPay' },
    'pay.ot': { helpKey: 'dashboard.kpi.overtimeHours' },
    'exp.pending': { helpKey: 'dashboard.kpi.pendingClaims' },
    'exp.approved': { helpKey: 'dashboard.kpi.approvedMtd' },
    'exp.amount': { helpKey: 'dashboard.kpi.claimsAmount' },
    'exp.oldest': { helpKey: 'dashboard.kpi.longestWaiting' },
    'ws.approvals': { helpKey: 'dashboard.kpi.approvalsWaiting' },
    'ws.journalsAwaiting': { helpKey: 'dashboard.kpi.journalsAwaiting' },
    'ws.journalsPosted': { helpKey: 'dashboard.kpi.journalsPosted' },
    'ws.journalsReversed': { helpKey: 'dashboard.kpi.journalsReversed' },
    'ws.billsDue7': { helpKey: 'dashboard.kpi.billsDue7' },
    'ws.overdueBills': { helpKey: 'dashboard.kpi.overdueBills' },
    'ws.billsHold': { helpKey: 'dashboard.kpi.billsHold' },
    'ws.runsDraft': { helpKey: 'dashboard.kpi.runsDraft' },
    'ws.runsAwaiting': { helpKey: 'dashboard.kpi.runsAwaiting' },
    'ws.issuedCheques': { helpKey: 'dashboard.kpi.issuedCheques' },
    'ws.receivedPdc': { helpKey: 'dashboard.kpi.receivedPdc' },
    'ws.overdueInvoices': { helpKey: 'dashboard.kpi.overdueInvoices' },
    'ws.ar60': { helpKey: 'dashboard.kpi.receivable60' },
    'ws.promises': { helpKey: 'dashboard.kpi.promisesDue' },
    'ws.unreconciled': { helpKey: 'dashboard.kpi.unreconciled' },
    'ws.nextDeadline': { helpKey: 'dashboard.kpi.nextDeadline' },
    'ws.lastClosed': { helpKey: 'dashboard.kpi.lastClosed' },
};

/** Action Center item id to its help key. `payroll.awaiting.<yyyy-m>` is matched by prefix in `actionHelpKey`. */
export const ACTION_HELP: Record<string, { helpKey: string }> = {
    'leave.overdue': { helpKey: 'dashboard.action.leaveOverdue' },
    'leave.pending': { helpKey: 'dashboard.action.leavePending' },
    'expense.pending': { helpKey: 'dashboard.action.expensePending' },
    'requisition.pending': { helpKey: 'dashboard.action.requisitionPending' },
    'stock.low': { helpKey: 'dashboard.action.stockLow' },
    'ar.overdue': { helpKey: 'dashboard.action.receivableOver60' },
    'ar.over60': { helpKey: 'dashboard.action.receivableOver60' },
    'payroll.awaiting': { helpKey: 'dashboard.action.payrollAwaiting' },
    'compliance.due': { helpKey: 'dashboard.action.complianceDue' },
    'repairbill.waiting': { helpKey: 'dashboard.action.repairBillWaiting' },
    'approvals.waiting': { helpKey: 'dashboard.action.approvalsWaiting' },
    'journal.awaiting': { helpKey: 'dashboard.action.journalAwaiting' },
    'bill.overdue': { helpKey: 'dashboard.action.billOverdue' },
    'bill.hold': { helpKey: 'dashboard.action.billHold' },
    'bank.unreconciled': { helpKey: 'dashboard.action.bankUnreconciled' },
    'run.draft': { helpKey: 'dashboard.action.runDraft' },
    'run.awaiting': { helpKey: 'dashboard.action.runAwaiting' },
    'invoice.overdue': { helpKey: 'dashboard.action.invoiceOverdue' },
    'promise.due': { helpKey: 'dashboard.action.promiseDue' },
    'pdc.due': { helpKey: 'dashboard.action.pdcDue' },
};

/** The help key for an Action Center item id, or undefined for an id with no text (no tag is rendered). */
export function actionHelpKey(id: string): string | undefined {
    const base = id.startsWith('payroll.awaiting.') ? 'payroll.awaiting' : id;
    return ACTION_HELP[base]?.helpKey;
}

/** Page tag for each module board, by `boardKey` (literal keys, so the key-coverage scan reads them). */
export const MODULE_BOARD_PAGE: Record<string, { 'data-help-page': string }> = {
    attendance: { 'data-help-page': 'dashboard.page.attendance' },
    leave: { 'data-help-page': 'dashboard.page.leave' },
    people: { 'data-help-page': 'dashboard.page.people' },
    accounting: { 'data-help-page': 'dashboard.page.accounting' },
    inventory: { 'data-help-page': 'dashboard.page.inventory' },
    sales: { 'data-help-page': 'dashboard.page.sales' },
    payroll: { 'data-help-page': 'dashboard.page.payroll' },
    expense: { 'data-help-page': 'dashboard.page.expense' },
};
